import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LogOut, User } from 'lucide-react'

import { Avatar } from '@/components/common/Avatar'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { useAuth } from '@/hooks/useAuth'

export const UserMenuDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNavigation = (path: string) => {
    navigate(path)
    setIsOpen(false)
  }

  const handleLogoutClick = () => {
    setIsOpen(false)
    setShowLogoutConfirm(true)
  }

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true)
    try {
      await logout()
    } finally {
      setIsLoggingOut(false)
      setShowLogoutConfirm(false)
    }
  }

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 p-1 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Menú de usuario"
        >
          <Avatar name={user?.nombre} size="md" />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-sm font-medium text-gray-900">{user?.nombre || 'Usuario'}</div>
              <div className="text-sm text-gray-500 mt-0.5">{user?.email || ''}</div>
            </div>
            <div className="py-1">
              <button
                onClick={() => handleNavigation('/profile')}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <User className="h-4 w-4 mr-3" />
                Mi Perfil
              </button>
              <button
                onClick={handleLogoutClick}
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="h-4 w-4 mr-3" />
                Cerrar sesión
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogoutConfirm}
        title="Cerrar sesión"
        message="¿Está seguro que desea cerrar su sesión?"
        confirmText="Cerrar sesión"
        cancelText="Cancelar"
        isDangerous={false}
        isLoading={isLoggingOut}
      />
    </>
  )
}
